<!--
  * Path: packages/frontend/src/core/components/common/ToolbarSidebar.vue
  * Task: Reusable vertical action sidebar for entity meta-functions (History, Comments, Duplicate, Delete).
  * Update: Context-aware. Disables structural modifications if rendered inside a Preview Modal while preserving visibility. Emits @close instead of hardcoding store resets. Adds open in preview.
-->
<template>
  <div class="position-absolute right-0 top-0 h-100 pe-6 py-4 d-flex flex-column align-end pointer-events-none" style="z-index: 10;">
    <!-- overflow: visible !important; ensures that floating panels can extend outside the rounded card -->
    <v-card class="elevation-2 rounded-xl d-flex flex-column align-center py-2 pointer-events-auto" width="48" style="overflow: visible !important;">
      <slot>
        <!-- Default Entity Actions (Only shown if no custom slot content is provided) -->
        <template v-if="activeEntity">
          <!-- Close Detail Panel (Secondary Context Only) -->
          <template v-if="props.isSecondary && !props.hideClose">
            <v-btn
              icon
              variant="text"
              size="small"
              class="mb-2"
              style="color: rgb(var(--v-theme-on-surface)); opacity: 0.6;"
              @click="$emit('close')"
            >
              <v-icon>mdi-close</v-icon>
              <v-tooltip activator="parent" location="left">Close Details</v-tooltip>
            </v-btn>
            <v-divider class="w-50 mb-4" style="border-color: rgba(var(--v-border-color), var(--v-border-opacity));"></v-divider>
          </template>

          <!-- Open in Preview Modal -->
          <v-btn
            v-if="!isPreview"
            icon
            variant="text"
            size="small"
            class="mb-2"
            color="medium-emphasis"
            @click="openInPreview"
          >
            <v-icon>mdi-open-in-new</v-icon>
            <v-tooltip activator="parent" location="left">Open in Modal</v-tooltip>
          </v-btn>
          <v-divider v-if="!isPreview" class="w-50 mb-2 border-opacity-25"></v-divider>

          <!-- History Button -->
          <v-btn
            icon
            variant="text"
            size="small"
            class="mb-2"
            :color="showHistoryPanel ? 'primary' : 'medium-emphasis'"
            @click="showHistoryPanel = !showHistoryPanel"
          >
            <v-icon>mdi-history</v-icon>
            <v-tooltip activator="parent" location="left">History</v-tooltip>
          </v-btn>
          <FloatingPanelHistory v-model="showHistoryPanel" :is-secondary="props.isSecondary" />

          <!-- Comments Button -->
          <v-btn
            icon
            variant="text"
            size="small"
            class="mb-2"
            :color="showCommentsPanel ? 'primary' : 'medium-emphasis'"
            @click="showCommentsPanel = !showCommentsPanel"
          >
            <v-badge :content="commentsCount" :model-value="commentsCount > 0" color="primary" offset-x="4" offset-y="4">
              <v-icon>mdi-comment-outline</v-icon>
            </v-badge>
            <v-tooltip activator="parent" location="left">Comments</v-tooltip>
          </v-btn>
          <FloatingPanelComments v-model="showCommentsPanel" :is-secondary="props.isSecondary" />

          <v-divider class="w-50 my-2" style="border-color: rgba(var(--v-border-color), var(--v-border-opacity));"></v-divider>

          <!-- Create Predecessor -->
          <v-btn
            v-if="canEditWorkspace && supportsWorkflow"
            icon
            variant="text"
            size="small"
            color="medium-emphasis"
            class="mb-2 mt-2"
            :disabled="isPreview"
            @click="handleCreatePredecessor"
          >
            <v-icon>mdi-arrow-up-circle-outline</v-icon>
            <v-tooltip activator="parent" location="left">Add Predecessor</v-tooltip>
          </v-btn>

          <!-- New Entity of same type Button -->
          <v-btn
            v-if="canEditWorkspace"
            icon
            variant="text"
            size="small"
            color="medium-emphasis"
            class="mb-2"
            :class="{ 'mt-2': !supportsWorkflow }"
            :disabled="isPreview"
            @click="handleCreateNew"
          >
            <v-icon>mdi-plus</v-icon>
            <v-tooltip activator="parent" location="left">New {{ activeEntity?.entityType ? activeEntity.entityType.charAt(0).toUpperCase() + activeEntity.entityType.slice(1) : 'Entity' }}</v-tooltip>
          </v-btn>

          <!-- Create Successor -->
          <v-btn
            v-if="canEditWorkspace && supportsWorkflow"
            icon
            variant="text"
            size="small"
            color="medium-emphasis"
            class="mb-2"
            :disabled="isPreview"
            @click="handleCreateSuccessor"
          >
            <v-icon>mdi-arrow-down-circle-outline</v-icon>
            <v-tooltip activator="parent" location="left">Add Successor</v-tooltip>
          </v-btn>

          <!-- Duplicate Button -->
          <v-btn
            v-if="canEditWorkspace"
            icon
            variant="text"
            size="small"
            color="medium-emphasis"
            class="mb-2"
            :disabled="isPreview"
            @click="handleDuplicate"
          >
            <v-icon>mdi-content-copy</v-icon>
            <v-tooltip activator="parent" location="left">Duplicate</v-tooltip>
          </v-btn>

          <!-- Split Button -->
          <v-btn
            v-if="canEditWorkspace && supportsWorkflow"
            icon
            variant="text"
            size="small"
            color="medium-emphasis"
            class="mb-2"
            :disabled="isPreview"
            @click="handleSplit"
          >
            <v-icon>mdi-call-split</v-icon>
            <v-tooltip activator="parent" location="left">Split (Start & End)</v-tooltip>
          </v-btn>

          <v-spacer></v-spacer>

          <!-- Delete Button -->
          <v-btn
            v-if="canEditWorkspace"
            icon
            variant="text"
            size="small"
            color="error"
            class="mt-2"
            :disabled="isPreview"
            @click="confirmDelete"
          >
            <v-icon>mdi-delete-outline</v-icon>
            <v-tooltip activator="parent" location="left">Delete Entity</v-tooltip>
          </v-btn>
        </template>
      </slot>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useAuthStore } from '@/store/auth';
import { useEntityOperations } from '@/core/composables/useEntityOperations';
import { usePermissions } from '@/core/composables/usePermissions';
import type { Entity } from '@/core/types/editor';
import { FormContextKey } from '@/core/keys';

import FloatingPanelComments from '@/core/components/floatingPanel/FloatingPanelComments.vue';
import FloatingPanelHistory from '@/core/components/floatingPanel/FloatingPanelHistory.vue';

interface Props {
  isSecondary?: boolean;
  hideClose?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isSecondary: false,
  hideClose: false
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const workspaceStore = useWorkspaceStore();
const authStore = useAuthStore();
const { canEditWorkspace } = usePermissions();
const { createNewEntity, duplicateEntity, splitEntity, deleteEntity } = useEntityOperations();
const formContext = inject(FormContextKey, null);

const showCommentsPanel = ref<boolean>(false);
const showHistoryPanel = ref<boolean>(false);

// Context-Aware Entity Resolution
const activeEntity = computed<Entity | null>(() => {
  if (formContext && formContext.entity.value) return formContext.entity.value;
  return props.isSecondary ? workspaceStore.secondarySelectedEntity : workspaceStore.selectedEntity;
});

// Determine if we are currently rendering inside the Preview Modal Context
const isPreview = computed<boolean>(() => {
  return workspaceStore.isPreviewModalOpen && formContext?.entityId.value === workspaceStore.previewEntityId;
});

const commentsCount = computed(() => {
  return activeEntity.value?.comments?.length || 0;
});

const supportsWorkflow = computed(() => {
  if (!activeEntity.value?.entityType) return false;
  const rels = authStore.appData?.relations || [];
  const predRel = rels.find((r: any) => r.id === 'predecessors');
  if (!predRel) return false;
  return predRel.sourceTypes?.includes(activeEntity.value.entityType) || predRel.targetTypes?.includes(activeEntity.value.entityType);
});

const openInPreview = () => {
  if(activeEntity.value?.id) {
    workspaceStore.setPreviewEntity(activeEntity.value.id);
  }
}

const handleCreateNew = (): void => {
  if (!activeEntity.value?.entityType) return;
  createNewEntity(activeEntity.value.entityType);
};

const handleCreatePredecessor = () => {
  if (!activeEntity.value?.entityType || !activeEntity.value?.id) return;
  createNewEntity(activeEntity.value.entityType, {
    successorIds: [activeEntity.value.id]
  });
};

const handleCreateSuccessor = () => {
  if (!activeEntity.value?.entityType || !activeEntity.value?.id) return;
  createNewEntity(activeEntity.value.entityType, {
    predecessorIds: [activeEntity.value.id]
  });
};

const handleDuplicate = async (): Promise<void> => {
  await duplicateEntity(props.isSecondary);
};

const handleSplit = async (): Promise<void> => {
  if (confirm(`Do you want to split "${activeEntity.value?.title}" into a Start and End entity?`)) {
    await splitEntity(props.isSecondary);
  }
};

const confirmDelete = (): void => {
  if (confirm(`Do you really want to delete the entity "${activeEntity.value?.title}" and all its relationships? This action cannot be undone.`)) {
    deleteEntity(props.isSecondary);
  }
};
</script>

<style scoped>
.pointer-events-none {
  pointer-events: none;
}

.pointer-events-auto {
  pointer-events: auto;
}

/* Force elements injected via <slot> from the Settings views to scale perfectly into the pill */
:deep(.v-btn.mb-4) {
  margin-bottom: 8px !important;
}

:deep(.v-btn.mt-2) {
  margin-top: 8px !important;
}

:deep(.v-btn) {
  width: 32px !important;
  height: 32px !important;
}
</style>
